import { showSignUpModalAtom } from '@/atoms/chatAtoms';
import {
  Banner,
  BannerAction,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { cn } from '@/lib/cn';
import { type WidgetMessage } from '@/lib/embed.utils';
import { useTrackSignUpWallViewed } from '@/hooks/useTrackSignUpWallViewed';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

interface EvidenceSignUpBannerProps {
  className?: string;
  onMaybeLater?: () => void;
  show?: boolean;
}

export const EvidenceSignUpBanner = ({ className, onMaybeLater, show }: EvidenceSignUpBannerProps) => {
  const trackSignUpWallViewed = useTrackSignUpWallViewed();
  const setShowSignUpModal = useSetAtom(showSignUpModalAtom);

  const postToParent = useCallback((message: WidgetMessage) => {
    try {
      if (window.parent !== window) {
        window.parent.postMessage(message, '*');
      }
    } catch (e) {
      console.error('[Embed] Failed to post message to parent:', e);
    }
  }, []);

  const onSignup = () => {
    trackSignUpWallViewed(EvidenceSignUpWall.BannerWall);
    setShowSignUpModal(true);
    postToParent({ type: 'heidi:modalOpened' });
  };

  if (!show) return null;

  return (
    <Banner
      role="banner"
      subVariant="default"
      className={cn(
        'flex-col items-stretch gap-y-3 shadow-sm sm:flex-row sm:items-center sm:gap-y-0',
        className
      )}
    >
      <BannerContent>
        <BannerTitle>
          <FormattedMessage defaultMessage="Save your chats, and start new ones." id="e93f+zVGeE" />
        </BannerTitle>
        <BannerDescription>
          <FormattedMessage defaultMessage="Access chat history, control your sources, and connect patient notes." id="9ki9Sc7nRa" />
        </BannerDescription>
      </BannerContent>
      <BannerAction>
        <Button variant="default" size="md" onClick={onSignup}>
          <FormattedMessage defaultMessage="Sign up free" id="cPh9OCFhsM" />
        </Button>
        <Button variant="ghost" size="md" onClick={() => onMaybeLater?.()}>
          <FormattedMessage defaultMessage="Maybe later" id="2nz+k9xHZw" />
        </Button>
      </BannerAction>
    </Banner>
  );
};
